import React, { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import useSWR from 'swr'
import { create } from 'zustand'
import { fetcher } from 'api/swr'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import Loader from '@/components/ui/Loader'
import EmptyDatas from 'components/ui/EmptyDatas'
import Table from 'components/ui/my-data-table'
import { columns } from './data-table-column'
import DataTableToolbar from './data-table-toolbar'

const PAGE_SIZE = 100

const parseIds = (value) =>
  [
    ...new Set(
      String(value || '')
        .split(/[\s,，;；]+/)
        .map((item) => item.trim())
        .filter((item) => item.length > 0),
    ),
  ]

const fetchTemplatesByIds = ([, ids, params]) =>
  fetcher(['/templates/batch', { ...params, ids: ids.join(','), filter: true }])

export const useForm = create((set) => ({
  after: null,
  afters: [],
  tid: '',
  publisher: '',
  status: null,
  hasDlc: false,
  aspectRatio: '',
  setAspectRatio: (aspectRatio) => set({ aspectRatio, hasDlc: false, after: null, afters: [] }),
  setTid: (tid) => set({ tid, after: null, afters: [] }),
  setPublisher: (publisher) =>
    set({ publisher: publisher || '', after: null, afters: [] }),
  setStatus: (status) =>
    set({
      status: status === null || status === undefined || status === '' ? null : status,
      after: null,
      afters: [],
    }),
  setHasDlc: (hasDlc) => set((state) => ({ hasDlc, aspectRatio: hasDlc ? '' : state.aspectRatio, after: null, afters: [] })),
  goAfter: (nextAfter) =>
    set((state) => {
      if (nextAfter === null) {
        return { after: null }
      }
      const index = state.afters.findIndex((a) => a === nextAfter)
      if (index === -1) {
        return { after: nextAfter, afters: [...state.afters, nextAfter] }
      } else {
        return { after: nextAfter }
      }
    }),
}))

const AllTemplates = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const setPublisher = useForm((s) => s.setPublisher)

  // 支持从 URL 带入，例如 /admin?tplTab=templates&publisher=xxx
  useEffect(() => {
    const publisher = searchParams.get('publisher')
    setPublisher(publisher || '')
  }, [searchParams, setPublisher])

  const filterByPublisher = (publisherId) => {
    const next = publisherId || ''
    setPublisher(next)
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        if (next) params.set('publisher', next)
        else params.delete('publisher')
        return params
      },
      { replace: true },
    )
  }

  const tidInput = useForm((s) => s.tid)
  const tids = useMemo(() => parseIds(tidInput), [tidInput])
  const isMultiIdQuery = tids.length > 1

  const params = useForm((s) => {
    const { after, tid, publisher, status, hasDlc, aspectRatio } = s
    const ids = parseIds(tid)
    const hasStatus = status !== null && status !== undefined && status !== ''
    return {
      after,
      tid: ids.length === 1 ? ids[0] : '',
      limit: PAGE_SIZE,
      // 后端 author 参数实际按 painter（真人 Publisher）过滤
      ...(publisher?.trim() ? { author: publisher.trim() } : {}),
      // Draft 的 status=0 也要传；未选时不传
      ...(hasStatus ? { status: Number(status) } : {}),
      hasDlc: hasDlc || undefined,
      ...(aspectRatio !== '' ? { aspectRatio: String(aspectRatio) } : {}),
    }
  })

  const listQuery = useSWR(isMultiIdQuery ? null : ['/templates', params])
  const multiIdQuery = useSWR(
    isMultiIdQuery ? ['/templates/by-ids', tids, params] : null,
    fetchTemplatesByIds,
  )
  const { data, error, isLoading, isValidating, mutate } = isMultiIdQuery
    ? multiIdQuery
    : listQuery
  const nextAfter =
    data?.length >= PAGE_SIZE ? data[data.length - 1]?.id : undefined

  const table = useReactTable({
    data,
    columns,
    enableRowSelection: false,
    getRowId: (row) => row.id,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      onPublisherClick: (publisherId) => {
        if (publisherId) filterByPublisher(publisherId)
      },
    },
  })

  if (isLoading || isValidating) return <Loader>Load templates</Loader>
  if (!data && !error) return <EmptyDatas />

  return (
    <div className="flex flex-col h-[calc(100vh-150px)]">
      <div className="flex-shrink-0 mb-2">
        <DataTableToolbar mutate={mutate} nextAfter={nextAfter} />
      </div>
      <div className="flex-1 min-h-0">
        <Card className="h-full flex flex-col">
          <CardContent className="flex-1 overflow-hidden pl-4 pr-0 pb-0">
            <div className="h-full overflow-auto">
              {error ? <div role="alert">加载模板失败：{error.message || '请重试'}</div> : <Table table={table} />}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

export default AllTemplates
